// P1.6 — "Ver como…" seguro para Super Admin.
//
// SOLO presentación. No cambia el usuario autenticado, ni la suscripción en la
// base de datos, ni RLS, ni Stripe, ni la autorización del servidor. Las
// mutaciones reales siguen gobernadas por los entitlements reales.
//
// Persistencia: sessionStorage (sobrevive a recargas, se pierde al cerrar la
// sesión del navegador). Nunca en perfil ni en base de datos.

export type PlanPreviewMode = 'off' | 'free' | 'pro';

export const PLAN_PREVIEW_STORAGE_KEY = 'plaxter-plan-preview';

export const PLAN_PREVIEW_LABELS: Record<Exclude<PlanPreviewMode, 'off'>, string> = {
  free: 'VISTA DEMO · PLAN GRATIS',
  pro: 'VISTA DEMO · PLAN PRO',
};

/** Subconjunto de `get_user_access_context` que la UI usa para gating. */
export interface PreviewableAccessContext {
  effective_plan: string;
  access_source: 'stripe' | 'manual' | 'free' | 'suspended';
  is_paying_customer: boolean;
  stripe_tier: string | null;
  stripe_end: string | null;
  max_items: number;
  max_categories: number;
  has_analytics: boolean;
  has_promotions: boolean;
  has_custom_domain: boolean;
}

/**
 * Presets deterministas tomados de la tabla real `plan_limits`
 * (free: 10/3; premium: 200/20 con analítica y promociones).
 */
export const PLAN_PREVIEW_PRESETS: Record<Exclude<PlanPreviewMode, 'off'>, PreviewableAccessContext> =
  Object.freeze({
    free: Object.freeze({
      effective_plan: 'free',
      access_source: 'free',
      is_paying_customer: false,
      stripe_tier: null,
      stripe_end: null,
      max_items: 10,
      max_categories: 3,
      has_analytics: false,
      has_promotions: false,
      has_custom_domain: false,
    }),
    pro: Object.freeze({
      effective_plan: 'premium',
      access_source: 'stripe',
      is_paying_customer: true,
      stripe_tier: 'premium',
      stripe_end: null,
      max_items: 200,
      max_categories: 20,
      has_analytics: true,
      has_promotions: true,
      has_custom_domain: false,
    }),
  }) as Record<Exclude<PlanPreviewMode, 'off'>, PreviewableAccessContext>;

/**
 * Normaliza un valor de storage. Un usuario que NO es Super Admin verificado
 * jamás activa la vista demo, aunque manipule sessionStorage a mano.
 */
export function parsePlanPreviewMode(raw: unknown, isAdmin: boolean): PlanPreviewMode {
  if (!isAdmin) return 'off';
  if (raw === 'free' || raw === 'pro') return raw;
  return 'off';
}

export function readPlanPreviewMode(isAdmin: boolean): PlanPreviewMode {
  if (!isAdmin) return 'off';
  try {
    return parsePlanPreviewMode(sessionStorage.getItem(PLAN_PREVIEW_STORAGE_KEY), isAdmin);
  } catch {
    return 'off';
  }
}

export function writePlanPreviewMode(mode: PlanPreviewMode, isAdmin: boolean): PlanPreviewMode {
  const safe = parsePlanPreviewMode(mode, isAdmin);
  try {
    if (safe === 'off') sessionStorage.removeItem(PLAN_PREVIEW_STORAGE_KEY);
    else sessionStorage.setItem(PLAN_PREVIEW_STORAGE_KEY, safe);
  } catch {
    /* almacenamiento no disponible: la vista demo simplemente no persiste */
  }
  return safe;
}

/**
 * Superpone el plan simulado sobre el contexto real de acceso.
 * Con `off` devuelve exactamente el contexto real (identidad).
 */
export function applyPlanPreview<T extends PreviewableAccessContext>(
  real: T | null,
  mode: PlanPreviewMode,
): T | PreviewableAccessContext | null {
  if (mode === 'off') return real;
  return { ...PLAN_PREVIEW_PRESETS[mode] };
}
